import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Box, Group, Stack, Alert } from '@mantine/core'
import { AppBadge, AppButton, AppCard, AppSwitch, AppText } from '../../app/common/ui'
import { FiSettings } from 'react-icons/fi'
import { toast } from 'react-toastify'
import agent from '../../app/api/agent'
import { useStore } from '../../app/stores/store'
import type { FeatureFlagDto } from '../../app/models/featureFlag'

const CUT_OVER_ORDER: Record<string, number> = {
  read: 0,
  'dual-write': 1,
  'write-only': 2
}

function sortCommentFlags (a: FeatureFlagDto, b: FeatureFlagDto): number {
  const rank = (key: string) => {
    if (key.endsWith('-read')) return CUT_OVER_ORDER.read
    if (key.endsWith('-dual-write')) return CUT_OVER_ORDER['dual-write']
    if (key.endsWith('-write-only')) return CUT_OVER_ORDER['write-only']
    return 99
  }
  return rank(a.key) - rank(b.key) || a.key.localeCompare(b.key)
}

function groupFeatureFlags (flags: FeatureFlagDto[]): { title: string; hint?: string; flags: FeatureFlagDto[] }[] {
  const slop = flags
    .filter((f) => f.key.startsWith('comments-service-') && !f.key.includes('-blog-'))
    .sort(sortCommentFlags)
  const blog = flags
    .filter((f) => f.key.startsWith('comments-service-blog-'))
    .sort(sortCommentFlags)
  const builds = flags
    .filter((f) => f.key.startsWith('builds-') || f.key.startsWith('slopify-'))
    .sort((a, b) => a.key.localeCompare(b.key))
  const community = flags
    .filter((f) => f.key === 'polls-enabled' || f.key === 'caches-enabled')
    .sort((a, b) => a.key.localeCompare(b.key))
  const vendor = flags
    .filter((f) => f.key.startsWith('vendor-'))
    .sort((a, b) => a.key.localeCompare(b.key))
  const firepower = flags
    .filter((f) => f.key.startsWith('firepower-'))
    .sort((a, b) => a.key.localeCompare(b.key))
  const other = flags
    .filter(
      (f) =>
        !f.key.startsWith('comments-service-') &&
        !f.key.startsWith('builds-') &&
        !f.key.startsWith('slopify-') &&
        !f.key.startsWith('vendor-') &&
        !f.key.startsWith('firepower-') &&
        f.key !== 'polls-enabled' &&
        f.key !== 'caches-enabled'
    )
    .sort((a, b) => a.key.localeCompare(b.key))

  const groups: { title: string; hint?: string; flags: FeatureFlagDto[] }[] = []
  if (slop.length > 0) {
    groups.push({
      title: 'Comments service — Slop feed',
      hint: 'Cutover order: dual-write (soak) → read + write-only → disable dual-write. See docs/COMMENTS_SERVICE_MIGRATION.md.',
      flags: slop
    })
  }
  if (blog.length > 0) {
    groups.push({
      title: 'Comments service — Blog posts',
      hint: 'Uses blog-specific flags; independent from Slop. Same soak order as Slop.',
      flags: blog
    })
  }
  if (builds.length > 0) {
    groups.push({
      title: 'Builds V2 & Slopify',
      hint: 'Phase 1-2: audit logging, zone positioning, version history. Slopify: schematic generation with phased rollout.',
      flags: builds
    })
  }
  if (community.length > 0) {
    groups.push({
      title: 'Community (v0.6)',
      hint: 'Apply migration AddProductRoadmapV06Scaffolding before enabling. Polls can ship before Caches (Phase 0).',
      flags: community
    })
  }
  if (vendor.length > 0) {
    groups.push({
      title: 'Vendor integration',
      hint: 'Phase 1: FFL/dealer CSV. Phase 2: real-time API + conversion tracking. Kill switch: set Vendor:ListingVisibilityPercent to 0 in appsettings.',
      flags: vendor
    })
  }
  if (firepower.length > 0) {
    groups.push({
      title: 'Firepower (Premium cosmetics)',
      hint: 'Credits via Stripe purchase, referral program, cosmetics shop. Cosmetics-only; no P2P gifting.',
      flags: firepower
    })
  }
  if (other.length > 0) {
    groups.push({ title: 'Product & platform', flags: other })
  }
  return groups
}

interface ExternalServiceStatus {
  [key: string]: boolean
}

export function FeatureFlagsAdminPanel () {
  const { commonStore } = useStore()
  const [featureFlags, setFeatureFlags] = useState<FeatureFlagDto[]>([])
  const [externalServices, setExternalServices] = useState<ExternalServiceStatus>({})
  const [loading, setLoading] = useState(false)
  const [loadingServices, setLoadingServices] = useState(false)
  const [togglingKey, setTogglingKey] = useState<string | null>(null)

  const loadFlags = useCallback(async () => {
    setLoading(true)
    try {
      const list = await agent.FeatureFlags.list()
      setFeatureFlags(list)
      await commonStore.loadFeatureFlags()
    } catch {
      setFeatureFlags([])
      toast.error('Failed to load feature flags')
    } finally {
      setLoading(false)
    }
  }, [commonStore])

  const loadExternalServices = useCallback(async () => {
    setLoadingServices(true)
    try {
      const response = await agent.Admin.getExternalServiceStatus()
      setExternalServices(response)
    } catch {
      setExternalServices({})
      toast.error('Failed to load external service status')
    } finally {
      setLoadingServices(false)
    }
  }, [])

  useEffect(() => {
    void loadFlags()
    void loadExternalServices()
  }, [loadFlags, loadExternalServices])

  const groups = useMemo(() => groupFeatureFlags(featureFlags), [featureFlags])

  const serviceLabels: Record<string, string> = {
    ai: 'AI Assist',
    'photo-upload': 'Photo Uploads',
    'video-upload': 'Video Uploads',
    email: 'Email Notifications',
    'external-comments': 'External Comments Service'
  }

  return (
    <Stack gap="lg">
      <Alert icon={<FiSettings size={16} />} color="blue" title="Citadel resilience gates">
        External service status (read-only). Managed via config Citadel:ExternalServices.
        Gates default to enabled in production. Disable via appsettings to gracefully degrade when services are offline.
      </Alert>

      {Object.keys(externalServices).length > 0 && (
        <Box>
          <AppText weight={600} size="sm" mb={4}>
            External Services (Phase 2 gates)
          </AppText>
          <AppText size="xs" color="dimmed" mb="md" sx={{ lineHeight: 1.5 }}>
            Configuration-driven gates for external integrations. Set to false in appsettings to gracefully handle service outages.
          </AppText>
          <Stack spacing="md">
            {Object.entries(externalServices).map(([key, isEnabled]) => (
              <AppCard
                key={key}
                withBorder
                p="md"
                sx={{
                  background: 'var(--gs-surface2)',
                  borderColor: isEnabled ? 'var(--gs-accent)' : 'var(--gs-border)',
                  opacity: isEnabled ? 1 : 0.7
                }}
              >
                <Group position="apart" align="flex-start">
                  <Box sx={{ flex: 1, pr: 'md' }}>
                    <Group align="center" mb={4}>
                      <AppText size="sm" weight={600} c="var(--gs-text)">
                        {serviceLabels[key] || key}
                      </AppText>
                      <AppBadge color={isEnabled ? 'green' : 'gray'} variant="light" size="sm">
                        {isEnabled ? 'ENABLED' : 'DISABLED'}
                      </AppBadge>
                    </Group>
                    <AppText size="xs" color="dimmed">
                      {isEnabled ? 'External service is active and integrated' : 'External service is disabled; core flows continue without this integration'}
                    </AppText>
                  </Box>
                </Group>
              </AppCard>
            ))}
          </Stack>
        </Box>
      )}

      <Alert icon={<FiSettings size={16} />} color="blue" title="Feature flags">
        Toggle system behaviors without a deploy. Flags are seeded from the API and stored in the database.
        After changing Comments service flags, run backfill endpoints before enabling read/write-only (see migration doc).
      </Alert>

      <Group position="apart">
        <AppText size="sm" color="dimmed">
          {featureFlags.length} flag{featureFlags.length === 1 ? '' : 's'} loaded
        </AppText>
        <AppButton size="xs" variant="light" leftIcon={<FiSettings size={14} />} loading={loading} onClick={() => void loadFlags()}>
          Refresh
        </AppButton>
      </Group>

      {loading && featureFlags.length === 0
        ? (
        <AppText size="sm" color="dimmed">
          Loading feature flags…
        </AppText>
          )
        : featureFlags.length === 0
          ? (
        <AppText size="sm" color="dimmed">
          No feature flags found.
        </AppText>
            )
          : (
        <Stack spacing="xl">
          {groups.map((group) => (
            <Box key={group.title}>
              <AppText weight={600} size="sm" mb={4}>
                {group.title}
              </AppText>
              {group.hint && (
                <AppText size="xs" color="dimmed" mb="md" sx={{ lineHeight: 1.5 }}>
                  {group.hint}
                </AppText>
              )}
              <Stack spacing="md">
                {group.flags.map((flag) => (
                  <AppCard
                    key={flag.key}
                    withBorder
                    p="md"
                    sx={{
                      background: 'var(--gs-surface2)',
                      borderColor: flag.isEnabled ? 'var(--gs-accent)' : 'var(--gs-border)'
                    }}
                  >
                    <Group position="apart" align="flex-start">
                      <Box sx={{ flex: 1, pr: 'md' }}>
                        <Group align="center" mb={4}>
                          <AppText size="sm" weight={600} ff="monospace" c="var(--gs-text)">
                            {flag.key}
                          </AppText>
                          <AppBadge color={flag.isEnabled ? 'green' : 'gray'} variant="light" size="sm">
                            {flag.isEnabled ? 'ACTIVE' : 'INACTIVE'}
                          </AppBadge>
                        </Group>
                        {flag.description && (
                          <AppText size="sm" color="dimmed" sx={{ lineHeight: 1.5 }}>
                            {flag.description}
                          </AppText>
                        )}
                      </Box>

                      <AppSwitch
                        checked={flag.isEnabled}
                        onChange={async (event) => {
                          const newValue = event.currentTarget.checked
                          setTogglingKey(flag.key)
                          try {
                            await agent.FeatureFlags.set(flag.key, newValue)
                            toast.success(`"${flag.key}" ${newValue ? 'enabled' : 'disabled'}.`)
                            await loadFlags()
                          } catch {
                            toast.error(`Failed to update "${flag.key}"`)
                          } finally {
                            setTogglingKey(null)
                          }
                        }}
                        disabled={togglingKey !== null}
                        size="md"
                        color="green"
                        label={flag.isEnabled ? 'Enabled' : 'Disabled'}
                        labelPosition="left"
                      />
                    </Group>
                    {togglingKey === flag.key && (
                      <AppText size="xs" color="dimmed" mt={8}>
                        Updating…
                      </AppText>
                    )}
                  </AppCard>
                ))}
              </Stack>
            </Box>
          ))}
        </Stack>
            )}
    </Stack>
  )
}
